import { useState, useEffect } from "react"
import { fetchPersonalUsageSummary, fetchUsageSummary, UsageSummaryResponse } from "../../lib/api/tenant"

interface UsageSectionProps {
  isTenantAdmin?: boolean
}

interface BreakdownItem {
  group: string
  count: number
  tokens: number
  time_ms: number
}

interface SummaryData {
  total_events: number
  total_tokens: number
  total_processing_time_ms: number
  breakdown: BreakdownItem[]
}

function formatTokenCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
  return n.toLocaleString()
}

function formatCost(cost: number): string {
  return `$${cost.toFixed(2)}`
}

function getProgressColor(percentage: number): string {
  if (percentage >= 80) return "bg-error-500"
  if (percentage >= 60) return "bg-warning-500"
  return "bg-success-500"
}

function formatEventType(type: string): string {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

function getSummaryStats(data: UsageSummaryResponse | null) {
  if (!data?.summary) {
    return { totalEvents: 0, totalTokens: 0, plansGenerated: 0, apiCalls: 0 }
  }
  const summary = data.summary as unknown as SummaryData
  const breakdown = summary.breakdown ?? []
  const plansGenerated = breakdown.find((b) => b.group === "plan_generated")?.count ?? 0
  const apiCalls = breakdown.find((b) => b.group === "api_call")?.count ?? 0
  return {
    totalEvents: summary.total_events ?? 0,
    totalTokens: summary.total_tokens ?? 0,
    plansGenerated,
    apiCalls,
  }
}

export default function UsageSection({ isTenantAdmin }: UsageSectionProps) {
  const [personalUsage, setPersonalUsage] = useState<UsageSummaryResponse | null>(null)
  const [teamUsage, setTeamUsage] = useState<UsageSummaryResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const personal = await fetchPersonalUsageSummary()
        setPersonalUsage(personal)
        if (isTenantAdmin) {
          const team = await fetchUsageSummary({ group_by: "event_type" })
          setTeamUsage(team)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load usage data")
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isTenantAdmin])

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <h3 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-7">Usage & Billing</h3>
        <div className="space-y-4">
          <div className="h-8 w-full animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  const budgetSource = isTenantAdmin ? teamUsage : personalUsage
  const budget = budgetSource?.monthly_budget

  const personalStats = getSummaryStats(personalUsage)
  const teamStats = getSummaryStats(teamUsage)

  const teamSummaryData = teamUsage?.summary as unknown as SummaryData | undefined
  const teamBreakdown: BreakdownItem[] = teamSummaryData?.breakdown ?? []

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
      <h3 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-7">Usage & Billing</h3>

      {error && (
        <div className="mb-4 rounded-lg bg-error-50 p-3 dark:bg-error-500/15">
          <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
        </div>
      )}

      {/* Monthly Token Budget */}
      {budget && (
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Monthly Token Usage</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {formatTokenCount(budget.tokens_used)} / {formatTokenCount(budget.monthly_budget)} ({budget.percentage}%)
            </span>
          </div>
          <div className="h-3 w-full rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className={`h-3 rounded-full ${getProgressColor(budget.percentage)} transition-all`}
              style={{ width: `${Math.min(budget.percentage, 100)}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Resets{" "}
            {new Date(budget.billing_period_start).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        </div>
      )}

      {/* Personal Usage Cards */}
      <div className="mb-6">
        <h4 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Your Usage</h4>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">API Calls</p>
            <p className="mt-1 text-xl font-semibold text-gray-800 dark:text-white">
              {personalStats.apiCalls.toLocaleString()}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Tokens Used</p>
            <p className="mt-1 text-xl font-semibold text-gray-800 dark:text-white">
              {formatTokenCount(personalStats.totalTokens)}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Plans Generated</p>
            <p className="mt-1 text-xl font-semibold text-gray-800 dark:text-white">
              {personalStats.plansGenerated.toLocaleString()}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Est. Cost</p>
            <p className="mt-1 text-xl font-semibold text-gray-800 dark:text-white">
              {formatCost(personalStats.totalTokens * 0.000003)}
            </p>
          </div>
        </div>
      </div>

      {/* Team Usage (admin only) */}
      {isTenantAdmin && teamUsage && (
        <>
          <div className="mb-6">
            <h4 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Team Usage</h4>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">API Calls</p>
                <p className="mt-1 text-xl font-semibold text-gray-800 dark:text-white">
                  {teamStats.apiCalls.toLocaleString()}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Tokens Used</p>
                <p className="mt-1 text-xl font-semibold text-gray-800 dark:text-white">
                  {formatTokenCount(teamStats.totalTokens)}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Plans Generated</p>
                <p className="mt-1 text-xl font-semibold text-gray-800 dark:text-white">
                  {teamStats.plansGenerated.toLocaleString()}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Est. Cost</p>
                <p className="mt-1 text-xl font-semibold text-gray-800 dark:text-white">
                  {formatCost(teamStats.totalTokens * 0.000003)}
                </p>
              </div>
            </div>
          </div>

          {/* Usage by Type Table */}
          {teamBreakdown.length > 0 && (
            <div>
              <h4 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Usage by Type</h4>
              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                      <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Event Type</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Requests</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Tokens</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamBreakdown.map((item, i) => (
                      <tr key={i} className="border-b border-gray-100 dark:border-gray-700/50">
                        <td className="px-4 py-3 text-gray-800 dark:text-gray-200">{formatEventType(item.group)}</td>
                        <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                          {item.count.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                          {formatTokenCount(item.tokens)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                          {formatCost(item.tokens * 0.000003)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
